import { isValidElement, useMemo } from 'react'
import {
  OptionsObject,
  ProviderContext,
  SnackbarMessage,
  enqueueSnackbar as enqueueNotistackSnackbar,
  useSnackbar as useNotistackSnackbar,
} from 'notistack'

export * from 'notistack'

type EnqueueSnackbar = ProviderContext['enqueueSnackbar']

type EnqueueOptions = OptionsObject & { message?: SnackbarMessage }

// notistack decides the same way whether the first argument is the message or the options
const isOptions = (messageOrOptions: SnackbarMessage | EnqueueOptions): boolean =>
  typeof messageOrOptions === 'object' &&
  messageOrOptions !== null &&
  !Array.isArray(messageOrOptions) &&
  !isValidElement(messageOrOptions)

/**
 * Error snackbars carry the backend error detail and must not disappear on their own — they
 * stay on screen until the user dismisses them via the close button. An explicit `persist` or
 * `autoHideDuration` at the call site still wins.
 */
const persistErrors = (options: EnqueueOptions = {}): EnqueueOptions =>
  options.variant === 'error' &&
  options.persist === undefined &&
  options.autoHideDuration === undefined
    ? { ...options, persist: true }
    : options

const enqueueWithPersistentErrors = (
  enqueue: EnqueueSnackbar,
  messageOrOptions: SnackbarMessage | EnqueueOptions,
  options?: EnqueueOptions,
) =>
  isOptions(messageOrOptions)
    ? enqueue(persistErrors(messageOrOptions as EnqueueOptions))
    : enqueue(messageOrOptions as SnackbarMessage, persistErrors(options))

export const enqueueSnackbar = ((
  messageOrOptions: SnackbarMessage | EnqueueOptions,
  options?: EnqueueOptions,
) =>
  // read the notistack binding lazily, it is only set once a SnackbarProvider mounted
  enqueueWithPersistentErrors(
    enqueueNotistackSnackbar,
    messageOrOptions,
    options,
  )) as EnqueueSnackbar

export const useSnackbar = (): ProviderContext => {
  const { enqueueSnackbar: enqueue, closeSnackbar } = useNotistackSnackbar()

  return useMemo(
    () => ({
      enqueueSnackbar: ((
        messageOrOptions: SnackbarMessage | EnqueueOptions,
        options?: EnqueueOptions,
      ) => enqueueWithPersistentErrors(enqueue, messageOrOptions, options)) as EnqueueSnackbar,
      closeSnackbar,
    }),
    [enqueue, closeSnackbar],
  )
}
